import * as React from "react";
import { useState } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { getPersonalInfo } from "../../store/integration-process/selector";
import { useAppSelector } from "../../Hooks";
import IndicateAnInaccuracy from "../../Components/dialog/counter/indicate-an-inaccuracy";
import PageLayout from "../../Components/page-layout/PageLayout";
import { AppRoute } from "../../enums/enum";
import { brand } from "../../theme";

const formatDate = (d?: string | null) => (d != null ? dayjs(d).format("DD.MM.YYYY") : "Не указано");

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2} sx={{ py: 0.75 }}>
      <Typography color="text.secondary" sx={{ fontSize: 14 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 500, textAlign: "right" }}>{value ?? "—"}</Typography>
    </Stack>
  );
}

export default function Counters() {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogData] = useState({ TypePu: "", NumberPu: "" });
  const counters = useAppSelector(getPersonalInfo)?.counters;
  const navigate = useNavigate();

  return (
    <PageLayout
      title="Приборы учёта"
      subtitle="Все счётчики по выбранному лицевому счёту"
      actions={
        counters?.length > 0 && (
          <Button variant="contained" onClick={() => navigate(AppRoute.SendReadings)}>
            Передать показания
          </Button>
        )
      }
    >
      <Grid container spacing={2}>
        {counters?.length > 0 &&
          counters.map((item, index) => (
            <Grid item xs={12} md={6} lg={4} key={item.factoryNumber ?? index}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha(brand.main, 0.1),
                        color: brand.main,
                      }}
                    >
                      <SpeedOutlinedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
                        {item.typePu}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
                        № {item.factoryNumber}
                      </Typography>
                    </Box>
                  </Stack>
                  <GetStatus date={item.dateCheckNext} />
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: brand.background }}>
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      Последние показания · {formatDate(item.lastReadingDate)}
                    </Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                      {item.lastReadings} <span style={{ fontSize: 14, fontWeight: 500, color: brand.muted }}>{item.dimension}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    <Row label="Модель" value={[item.brand, item.model].filter((x) => !!x).join(" ")} />
                    <Divider />
                    <Row label="Дата поверки" value={formatDate(item.dateCheck)} />
                    <Divider />
                    <Row label="Следующая поверка" value={formatDate(item.dateCheckNext)} />
                    <Divider />
                    <Row label="Межповерочный интервал" value={item.interVerificationInterval ? `${item.interVerificationInterval} лет` : "—"} />
                    <Divider />
                    <Row label="Дата ввода" value={formatDate(item.installationDate)} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
      {(counters == null || counters?.length == 0) && (
        <Card sx={{ textAlign: "center", py: 5, px: 2 }}>
          <Typography variant="h6">Приборы учёта не найдены</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            По выбранному лицевому счёту нет зарегистрированных счётчиков.
          </Typography>
        </Card>
      )}
      <IndicateAnInaccuracy Open={openDialog} Close={setOpenDialog} NumberPu={dialogData.NumberPu} TypePu={dialogData.TypePu} />
    </PageLayout>
  );
}

function GetStatus({ date }: { date: string | undefined }) {
  if (!date) return null;

  const formats = ["DD.MM.YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"];
  const dateCheckNext = dayjs(date, formats);
  const today = dayjs();

  if (!dateCheckNext.isValid()) return null;
  const diffMonths = dateCheckNext.diff(today, "month", true);

  if (dateCheckNext.isBefore(today, "day")) {
    return <Chip size="small" color="error" label="Срок поверки истёк" />;
  }
  if (diffMonths < 2) {
    return <Chip size="small" color="warning" label="Скоро поверка" />;
  }
  return <Chip size="small" color="success" variant="outlined" label="Поверен" />;
}
